/**
 * Legal pages.
 *
 * Built ONLY from facts already stated on the existing site: a New York State
 * practice, licensed physicians, HIPAA compliance, appointment-only service,
 * the consultation form fields, the published address and the published email.
 * No new business facts are asserted here.
 *
 * The practice name is never hardcoded — it is interpolated from PRACTICE so
 * there is exactly one place in the codebase where the brand is defined.
 */

import { PRACTICE } from './site'

const BRAND = PRACTICE.nameTitle

export type LegalBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'ul'; items: string[] }

export type LegalDoc = {
  title: string
  updated: string
  intro: string
  blocks: LegalBlock[]
}

type DocPair = { en: LegalDoc; es: LegalDoc }

export const PRIVACY: DocPair = {
  en: {
    title: 'Privacy Policy',
    updated: 'Last updated: August 2026',
    intro: `${BRAND} is a professional medical practice operated by licensed physicians in New York State. This policy explains what this website collects, how it is used, and the choices available to you.`,
    blocks: [
      { type: 'h2', text: '1. Information we collect' },
      {
        type: 'p',
        text: 'This website collects only the information you choose to submit through the consultation request form:',
      },
      {
        type: 'ul',
        items: [
          'Full name',
          'Phone number',
          'Preferred language',
          'Service of interest',
          'An optional message',
        ],
      },
      {
        type: 'p',
        text: 'The consultation request form is a scheduling tool. It is not intended for medical records, diagnoses, or other sensitive health information. Please do not submit such information through this website.',
      },
      { type: 'h2', text: '2. How we use your information' },
      {
        type: 'ul',
        items: [
          'To contact you regarding your consultation request.',
          'To schedule, confirm, or reschedule an appointment.',
          'To respond to questions you send us.',
        ],
      },
      { type: 'h2', text: '3. Protected health information' },
      {
        type: 'p',
        text: 'Our practice complies with HIPAA privacy requirements in the handling of protected health information. Information you share during a private consultation is handled as part of your medical record and is governed by applicable federal and New York State law, not by this website policy.',
      },
      { type: 'h2', text: '4. Sharing of information' },
      {
        type: 'p',
        text: 'We do not sell your personal information. Information may be shared only with service providers that support the operation of the practice, or where required by law.',
      },
      { type: 'h2', text: '5. Data security' },
      {
        type: 'p',
        text: 'We apply reasonable administrative, technical, and physical safeguards to protect the information you submit. No method of transmission over the internet is completely secure.',
      },
      { type: 'h2', text: '6. Your choices' },
      {
        type: 'p',
        text: 'You may request access to, correction of, or deletion of the contact information you submitted through this website by writing to us at the email address published on this site.',
      },
      { type: 'h2', text: '7. Changes to this policy' },
      {
        type: 'p',
        text: 'We may update this policy from time to time. The date at the top of this page identifies the current version.',
      },
      { type: 'h2', text: '8. Contact' },
      {
        type: 'p',
        text: 'Questions about this policy may be sent to the email address listed in the footer of this website.',
      },
    ],
  },
  es: {
    title: 'Politica de Privacidad',
    updated: 'Ultima actualizacion: agosto de 2026',
    intro: `${BRAND} es una practica medica profesional operada por medicos con licencia en el Estado de Nueva York. Esta politica explica que informacion recopila este sitio web, como se utiliza y las opciones disponibles para usted.`,
    blocks: [
      { type: 'h2', text: '1. Informacion que recopilamos' },
      {
        type: 'p',
        text: 'Este sitio web recopila unicamente la informacion que usted decide enviar a traves del formulario de solicitud de consulta:',
      },
      {
        type: 'ul',
        items: [
          'Nombre completo',
          'Numero de telefono',
          'Idioma preferido',
          'Servicio de interes',
          'Un mensaje opcional',
        ],
      },
      {
        type: 'p',
        text: 'El formulario de solicitud de consulta es una herramienta de programacion. No esta destinado a registros medicos, diagnosticos u otra informacion medica sensible. Por favor no envie ese tipo de informacion a traves de este sitio web.',
      },
      { type: 'h2', text: '2. Como usamos su informacion' },
      {
        type: 'ul',
        items: [
          'Para comunicarnos con usted respecto a su solicitud de consulta.',
          'Para programar, confirmar o reprogramar una cita.',
          'Para responder a las preguntas que nos envie.',
        ],
      },
      { type: 'h2', text: '3. Informacion medica protegida' },
      {
        type: 'p',
        text: 'Nuestra practica cumple con los requisitos de privacidad de HIPAA en el manejo de informacion medica protegida. La informacion que comparte durante una consulta privada se maneja como parte de su expediente medico y se rige por la ley federal y del Estado de Nueva York aplicable, no por esta politica del sitio web.',
      },
      { type: 'h2', text: '4. Divulgacion de informacion' },
      {
        type: 'p',
        text: 'No vendemos su informacion personal. La informacion solo puede compartirse con proveedores de servicios que apoyan la operacion de la practica, o cuando la ley lo exija.',
      },
      { type: 'h2', text: '5. Seguridad de los datos' },
      {
        type: 'p',
        text: 'Aplicamos salvaguardas administrativas, tecnicas y fisicas razonables para proteger la informacion que usted envia. Ningun metodo de transmision por internet es completamente seguro.',
      },
      { type: 'h2', text: '6. Sus opciones' },
      {
        type: 'p',
        text: 'Puede solicitar acceso, correccion o eliminacion de la informacion de contacto que envio a traves de este sitio web escribiendonos a la direccion de correo publicada en este sitio.',
      },
      { type: 'h2', text: '7. Cambios a esta politica' },
      {
        type: 'p',
        text: 'Podemos actualizar esta politica ocasionalmente. La fecha al inicio de esta pagina indica la version vigente.',
      },
      { type: 'h2', text: '8. Contacto' },
      {
        type: 'p',
        text: 'Las preguntas sobre esta politica pueden enviarse a la direccion de correo que aparece en el pie de pagina de este sitio web.',
      },
    ],
  },
}

export const TERMS: DocPair = {
  en: {
    title: 'Terms of Use',
    updated: 'Last updated: August 2026',
    intro:
      'These terms govern your use of this website. By accessing the site you agree to them.',
    blocks: [
      { type: 'h2', text: '1. Purpose of this website' },
      {
        type: 'p',
        text: 'This website is informational and is provided to allow prospective patients to request a private consultation. It does not constitute medical advice.',
      },
      { type: 'h2', text: '2. No physician-patient relationship' },
      {
        type: 'p',
        text: 'Using this website or submitting a consultation request does not create a physician-patient relationship. That relationship is established only after a formal consultation with a licensed physician of the practice.',
      },
      { type: 'h2', text: '3. Consultation requests' },
      {
        type: 'p',
        text: 'Submitting the consultation request form does not confirm an appointment. A coordinator will contact you to schedule. Medical services are provided by appointment only.',
      },
      { type: 'h2', text: '4. Individual results' },
      {
        type: 'p',
        text: 'Aesthetic and surgical outcomes vary between patients. Candidacy for any procedure is determined by an individual medical evaluation. No specific result is promised or guaranteed.',
      },
      { type: 'h2', text: '5. Intellectual property' },
      {
        type: 'p',
        text: 'The content of this website, including text, images, and design, may not be reproduced without written authorization.',
      },
      { type: 'h2', text: '6. Limitation of liability' },
      {
        type: 'p',
        text: 'To the extent permitted by law, the practice is not liable for damages arising from the use of, or inability to use, this website. The site is provided as is, without warranties of any kind.',
      },
      { type: 'h2', text: '7. Governing law' },
      {
        type: 'p',
        text: 'These terms are governed by the laws of the State of New York.',
      },
      { type: 'h2', text: '8. Changes' },
      {
        type: 'p',
        text: 'These terms may be modified at any time. Changes take effect when posted on this page.',
      },
    ],
  },
  es: {
    title: 'Terminos de Uso',
    updated: 'Ultima actualizacion: agosto de 2026',
    intro:
      'Estos terminos rigen el uso de este sitio web. Al acceder al sitio usted los acepta.',
    blocks: [
      { type: 'h2', text: '1. Proposito de este sitio web' },
      {
        type: 'p',
        text: 'Este sitio web es informativo y se proporciona para que los pacientes potenciales puedan solicitar una consulta privada. No constituye consejo medico.',
      },
      { type: 'h2', text: '2. No existe relacion medico-paciente' },
      {
        type: 'p',
        text: 'El uso de este sitio web o el envio de una solicitud de consulta no crea una relacion medico-paciente. Esa relacion se establece unicamente despues de una consulta formal con un medico con licencia de la practica.',
      },
      { type: 'h2', text: '3. Solicitudes de consulta' },
      {
        type: 'p',
        text: 'Enviar el formulario de solicitud de consulta no confirma una cita. Un coordinador se comunicara con usted para programar. Los servicios medicos se brindan solo con cita previa.',
      },
      { type: 'h2', text: '4. Resultados individuales' },
      {
        type: 'p',
        text: 'Los resultados esteticos y quirurgicos varian entre pacientes. La candidatura para cualquier procedimiento se determina mediante una evaluacion medica individual. No se promete ni se garantiza ningun resultado especifico.',
      },
      { type: 'h2', text: '5. Propiedad intelectual' },
      {
        type: 'p',
        text: 'El contenido de este sitio web, incluidos textos, imagenes y diseno, no puede reproducirse sin autorizacion por escrito.',
      },
      { type: 'h2', text: '6. Limitacion de responsabilidad' },
      {
        type: 'p',
        text: 'En la medida permitida por la ley, la practica no es responsable por danos derivados del uso o la imposibilidad de uso de este sitio web. El sitio se proporciona tal cual, sin garantias de ningun tipo.',
      },
      { type: 'h2', text: '7. Ley aplicable' },
      {
        type: 'p',
        text: 'Estos terminos se rigen por las leyes del Estado de Nueva York.',
      },
      { type: 'h2', text: '8. Modificaciones' },
      {
        type: 'p',
        text: 'Estos terminos pueden modificarse en cualquier momento. Los cambios entran en vigor al publicarse en esta pagina.',
      },
    ],
  },
}

export const DISCLAIMER: DocPair = {
  en: {
    title: 'Medical Disclaimer',
    updated: 'Last updated: August 2026',
    intro:
      'This website does not constitute medical advice and is for informational purposes only.',
    blocks: [
      { type: 'h2', text: 'Informational purpose' },
      {
        type: 'p',
        text: 'The content published on this website is provided for general information about the practice and the procedures it offers. It is not a diagnosis, a treatment recommendation, or a substitute for a medical evaluation.',
      },
      { type: 'h2', text: 'Individual evaluation required' },
      {
        type: 'p',
        text: 'All procedures require an individual medical evaluation. Candidacy is determined during a private consultation with a licensed physician of the practice.',
      },
      { type: 'h2', text: 'Results vary' },
      {
        type: 'p',
        text: 'Individual results vary between patients. Nothing on this website should be read as a promise or guarantee of a particular outcome.',
      },
      { type: 'h2', text: 'Regulatory compliance' },
      {
        type: 'p',
        text: `${BRAND} operates as a professional medical practice under the laws of New York State. All physicians are licensed to practice medicine in New York. The practice complies with all applicable state and federal regulations, including HIPAA privacy requirements.`,
      },
      { type: 'h2', text: 'Seeking care' },
      {
        type: 'p',
        text: 'Do not delay seeking medical care based on information found on this or any other website.',
      },
    ],
  },
  es: {
    title: 'Aviso Medico',
    updated: 'Ultima actualizacion: agosto de 2026',
    intro:
      'Este sitio web no constituye consejo medico y es solo para fines informativos.',
    blocks: [
      { type: 'h2', text: 'Proposito informativo' },
      {
        type: 'p',
        text: 'El contenido publicado en este sitio web se proporciona como informacion general sobre la practica y los procedimientos que ofrece. No es un diagnostico, una recomendacion de tratamiento ni un sustituto de una evaluacion medica.',
      },
      { type: 'h2', text: 'Se requiere evaluacion individual' },
      {
        type: 'p',
        text: 'Todos los procedimientos requieren una evaluacion medica individual. La candidatura se determina durante una consulta privada con un medico con licencia de la practica.',
      },
      { type: 'h2', text: 'Los resultados varian' },
      {
        type: 'p',
        text: 'Los resultados individuales varian entre pacientes. Nada en este sitio web debe interpretarse como una promesa o garantia de un resultado particular.',
      },
      { type: 'h2', text: 'Cumplimiento regulatorio' },
      {
        type: 'p',
        text: `${BRAND} opera como una practica medica profesional bajo las leyes del Estado de Nueva York. Todos los medicos tienen licencia para ejercer la medicina en Nueva York. La practica cumple con todas las regulaciones estatales y federales aplicables, incluidos los requisitos de privacidad de HIPAA.`,
      },
      { type: 'h2', text: 'Busqueda de atencion' },
      {
        type: 'p',
        text: 'No retrase la busqueda de atencion medica basandose en informacion encontrada en este o en cualquier otro sitio web.',
      },
    ],
  },
}

export const ACCESSIBILITY: DocPair = {
  en: {
    title: 'Accessibility',
    updated: 'Last updated: August 2026',
    intro:
      'We want this website to be usable by as many people as possible, including people using assistive technology.',
    blocks: [
      { type: 'h2', text: 'Our approach' },
      {
        type: 'ul',
        items: [
          'Semantic headings and landmarks so screen readers can navigate the page.',
          'A skip-to-content link at the top of every page.',
          'Visible keyboard focus on all interactive elements.',
          'Text alternatives for meaningful images.',
          'Form fields with associated labels.',
          'Content available in English and Spanish.',
        ],
      },
      { type: 'h2', text: 'Feedback' },
      {
        type: 'p',
        text: 'If you encounter a barrier on this website, or need information in another format, please write to the email address published in the footer and we will assist you.',
      },
    ],
  },
  es: {
    title: 'Accesibilidad',
    updated: 'Ultima actualizacion: agosto de 2026',
    intro:
      'Queremos que este sitio web pueda ser utilizado por la mayor cantidad de personas posible, incluidas las personas que usan tecnologia de asistencia.',
    blocks: [
      { type: 'h2', text: 'Nuestro enfoque' },
      {
        type: 'ul',
        items: [
          'Encabezados y regiones semanticas para que los lectores de pantalla puedan navegar la pagina.',
          'Un enlace para saltar al contenido en la parte superior de cada pagina.',
          'Foco de teclado visible en todos los elementos interactivos.',
          'Alternativas de texto para las imagenes con significado.',
          'Campos de formulario con etiquetas asociadas.',
          'Contenido disponible en ingles y espanol.',
        ],
      },
      { type: 'h2', text: 'Comentarios' },
      {
        type: 'p',
        text: 'Si encuentra una barrera en este sitio web, o necesita informacion en otro formato, escriba a la direccion de correo publicada en el pie de pagina y le ayudaremos.',
      },
    ],
  },
}
